const PLAYBACK_OWNERS = Object.freeze({
  INLINE: 'inline',
  PLAYBLOCK: 'playblock',
  UNIVERSAL: 'universal-player',
  MINI: 'mini-player',
  PIP: 'picture-in-picture',
});

const initialPlaybackSession = Object.freeze({
  sessionId: null,
  mediaId: null,
  mediaType: null,
  owner: null,
  state: 'idle',
  position: 0,
  duration: 0,
  route: null,
  startedAt: null,
  updatedAt: null,
  transferHistory: [],
});

const safeTime = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const createSessionId = (mediaType, mediaId, now = Date.now()) => `${mediaType || 'media'}:${mediaId || 'unknown'}:${now}`;

const startPlaybackSession = ({ track, owner = PLAYBACK_OWNERS.PLAYBLOCK, route, now = Date.now() }) => ({
  ...initialPlaybackSession,
  sessionId: createSessionId(track?.type, track?.youtubeId || track?.id || track?.url, now),
  mediaId: track?.youtubeId || track?.id || track?.url || null,
  mediaType: track?.type || null,
  owner,
  state: 'loading',
  route: route || null,
  startedAt: new Date(now).toISOString(),
  updatedAt: new Date(now).toISOString(),
});

const transferPlaybackOwner = (session, { owner, reason, route, now = Date.now() }) => {
  if (!session.sessionId || !owner || owner === session.owner) {
    return route && route !== session.route
      ? { ...session, route, updatedAt: new Date(now).toISOString() }
      : session;
  }

  return {
    ...session,
    owner,
    route: route || session.route,
    updatedAt: new Date(now).toISOString(),
    transferHistory: [
      ...session.transferHistory,
      {
        from: session.owner,
        to: owner,
        reason: reason || 'ownership-transfer',
        transferredAt: new Date(now).toISOString(),
      },
    ],
  };
};

const playbackSessionReducer = (state, action) => {
  switch (action.type) {
    case 'START':
      return startPlaybackSession(action.payload);
    case 'SYNC':
      if (!state.sessionId) return state;
      return {
        ...state,
        state: action.payload.state || state.state,
        position: safeTime(action.payload.position ?? state.position),
        duration: safeTime(action.payload.duration ?? state.duration),
        updatedAt: new Date(action.payload.now || Date.now()).toISOString(),
      };
    case 'TRANSFER':
      return transferPlaybackOwner(state, action.payload);
    case 'ROUTE':
      return state.sessionId && action.payload.route !== state.route
        ? { ...state, route: action.payload.route, updatedAt: new Date(action.payload.now || Date.now()).toISOString() }
        : state;
    case 'END':
      return { ...initialPlaybackSession };
    default:
      return state;
  }
};

const supportsPictureInPicture = (documentRef = typeof document === 'undefined' ? null : document) => Boolean(
  documentRef?.pictureInPictureEnabled && typeof documentRef?.exitPictureInPicture === 'function',
);

export {
  PLAYBACK_OWNERS,
  initialPlaybackSession,
  playbackSessionReducer,
  safeTime,
  startPlaybackSession,
  supportsPictureInPicture,
  transferPlaybackOwner,
};
